import type { NarrativePOI, SpecimenDefinition } from '../../core/types';
import { buildSeashellSpecimen } from '../../procedural/seashells';
import { buildSpiralTowerSpecimen } from '../../procedural/staircases';
import { buildViolinSpecimen } from '../../procedural/violins';
import { buildTelescopeSpecimen } from '../../procedural/telescopes';

export const ISIDORA_POIS: NarrativePOI[] = [
  {
    id: 'overview',
    number: 'I',
    title: 'The Spiral Citadel of Isidora',
    italianSubtitle: 'Le città e la memoria · 2',
    motifLabel: 'Panoramic Overview',
    excerpt:
      '“When a man rides a long time through wild regions he feels the desire for a city. Finally he comes to Isidora…”',
    reflection:
      'Rising from a twilight basin along a logarithmic spiral, nine travertine towers wind skyward—where architecture, music, optics, and longing converge.',
    zh: {
      title: '伊西多拉的螺旋城邦',
      motifLabel: '全景總覽',
      shortTabLabel: '全景總覽',
      excerpt:
        '「當一個人騎馬穿過荒野許久，他便會渴望一座城市。最後，他來到了伊西多拉……」',
      reflection:
        '九座石灰華高塔沿著黃金對數螺旋自暮色潟湖升起——在這裡，建築、製琴聲學、觀星光學與旅人的渴望交織成一座立體的夢境。',
    },
    cameraPos: [25.0, 16.5, 29.0],
    cameraTarget: [0.0, 7.2, -1.5],
    suggestedTemporal: 0.15,
    fov: 44,
  },
  {
    id: 'spirals',
    number: 'II',
    title: 'Spiral Staircases & Seashells',
    italianSubtitle: 'Scale a chiocciola incrostate di chiocciole marine',
    motifLabel: 'Logarithmic Spirals & Nacre',
    excerpt:
      '“…a city where the buildings have spiral staircases encrusted with spiral seashells…”',
    reflection:
      'Cantilevered stone treads trace helicospiral ascents around the masonry shafts, inlaid with iridescent Nautilus and Wentletrap conches that catch the amber dusk.',
    zh: {
      title: '鑲嵌海螺的螺旋階梯',
      motifLabel: '對數螺旋與珍珠母貝',
      shortTabLabel: '螺旋海螺階梯',
      excerpt:
        '「……在那座城市裡，建築物都有鑲嵌著螺旋海螺的螺旋階梯……」',
      reflection:
        '懸挑而出的石階沿著塔身盤旋而上，欄杆與階沿鑲滿了散發珍珠母貝虹彩的鸚鵡螺與海獅螺，在琥珀色的黃昏中微微共鳴。',
    },
    cameraPos: [-12.2, 10.6, 5.8],
    cameraTarget: [-5.2, 8.2, -2.5],
    suggestedTemporal: 0.1,
    fov: 40,
  },
  {
    id: 'telescopes',
    number: 'III',
    title: 'The Brass Observatory',
    italianSubtitle: 'Dove si fabbricano a regola d’arte cannocchiali',
    motifLabel: 'Keplerian Optics & Starlight',
    excerpt:
      '“…where perfect telescopes and violins are made…”',
    reflection:
      'At the zenith terrace, three Keplerian brass refractors cradle achromatic lenses within armillary rings, projecting cones of starlight toward the twilight constellations.',
    zh: {
      title: '黃銅觀星台',
      motifLabel: '克卜勒光學與星軌',
      shortTabLabel: '黃銅觀星台',
      excerpt:
        '「……那裡以精湛的技藝製造完美的望遠鏡與小提琴……」',
      reflection:
        '在最高處的露台上，三具克卜勒黃銅折射望遠鏡懸於渾儀經緯環中，消色差透鏡向暮色星群投射出澄澈的星光錐。',
    },
    cameraPos: [5.4, 21.8, 3.8],
    cameraTarget: [-1.8, 19.8, -5.2],
    suggestedTemporal: 0.35,
    fov: 36,
  },
  {
    id: 'violins',
    number: 'IV',
    title: 'The Luthier’s Acoustic Loft',
    italianSubtitle: 'E violini di perfetta risonanza',
    motifLabel: 'Cremonese Violins & Strings',
    excerpt:
      '“…where perfect telescopes and violins are made…”',
    reflection:
      'Suspended beneath twin resonance arches, a Cremonese violin and six aerial harp strings bridge the towers. Pluck any string to send harmonic waves across the stone.',
    zh: {
      title: '製琴師的共鳴拱廊',
      motifLabel: '克雷莫納提琴與琴弦',
      shortTabLabel: '製琴共鳴拱廊',
      excerpt:
        '「……那裡以精湛的技藝製造完美的望遠鏡與小提琴……」',
      reflection:
        '雙重音柱拱門之下懸掛著克雷莫納小提琴與六條跨塔豎琴弦。點擊或滑過任何一條琴弦，即可撥響泛音並讓聲波漣漪傳遍石塔。',
    },
    cameraPos: [13.5, 11.2, 4.8],
    cameraTarget: [6.8, 9.8, -2.2],
    suggestedTemporal: 0.2,
    fov: 38,
  },
  {
    id: 'crossroads',
    number: 'V',
    title: 'The Foreigner’s Triple Loggia & Brawl Plaza',
    italianSubtitle: 'Il forestiero esitante e i combattimenti di galli',
    motifLabel: 'Hesitation & Ember Plaza',
    excerpt:
      '“…where the foreigner hesitating between two women always encounters a third, where cockfights degenerate into bloody brawls among the bettors.”',
    reflection:
      'Three arched bridges meet at the belvedere of hesitation, overlooking the sunken crimson arena where bettors’ lanterns flicker like restless embers.',
    zh: {
      title: '異鄉人的三岔連廊與鬥雞廣場',
      motifLabel: '猶豫連廊與篝火廣場',
      shortTabLabel: '三岔連廊與廣場',
      excerpt:
        '「……猶豫於兩個女人之間的異鄉人總是會遇見第三個女人；而在那裡，鬥雞總會演變成賭客之間的流血鬥毆。」',
      reflection:
        '三座拱橋在猶豫的圓形露台交會，俯瞰下方深陷的八角形廣場——鬥雞篝火如躁動的紅蓮餘燼般明滅搖曳。',
    },
    cameraPos: [-14.2, 6.4, 15.2],
    cameraTarget: [-4.5, 3.0, 4.5],
    suggestedTemporal: 0.05,
    fov: 42,
  },
  {
    id: 'wall',
    number: 'VI',
    title: 'The Wall of the Old Men',
    italianSubtitle: 'I desideri sono già ricordi',
    motifLabel: 'Desire Turned to Memory',
    excerpt:
      '“The dreamed-of city contained him as a young man; he arrives at Isidora in his old age. In the square there is the wall where the old men sit and watch the young go by; he is seated in a row with them. Desires are already memories.”',
    reflection:
      'Seen from the semicircular stone bench at the city’s edge, the golden fever of youth cools into nocturnal silver—where every desire is already a memory.',
    zh: {
      title: '老者之牆：慾望已成記憶',
      motifLabel: '慾望化作記憶',
      shortTabLabel: '老者之牆',
      excerpt:
        '「在夢中的城市裡他正年輕，抵達伊西多拉時他卻已年老。廣場上有一道老者們坐著看年輕人走過的牆；他與他們並排坐在一起。慾望已經是記憶。」',
      reflection:
        '從廣場邊緣的半圓形石牆望去，年輕時熾熱的金黃暮色冷卻為深邃的月光銀藍——在這裡，每一分渴望都已化作記憶。',
    },
    cameraPos: [11.8, 2.15, 13.6],
    cameraTarget: [3.2, 1.35, 4.2],
    suggestedTemporal: 0.95,
    fov: 45,
  },
];

export const ISIDORA_SPECIMENS: SpecimenDefinition[] = [
  {
    id: 'seashell',
    name: 'Helicospiral Seashells (Nautilus & Wentletrap)',
    category: 'Biomathematical Geometry',
    summary:
      'Parametric coiled shells generated via Raup’s (1966) logarithmic helicospiral equations with periodic sculptural varices and thin-film nacre interference.',
    zh: {
      name: '對數螺旋海螺（鸚鵡螺與海獅螺）',
      shortName: '對數螺旋海螺',
      category: '生物數學幾何',
      summary:
        '以 Raup (1966) 對數螺旋曲面方程程序化生成之平面螺旋鸚鵡螺與高塔階梯海獅螺，搭配薄膜干涉珍珠母貝 Shader。',
    },
    formula: 'r(θ) = r₀·e^(bθ),  z(θ) = h·(e^(bθ) − 1) + A·cos³(kθ)',
    cameraPos: [0.0, 2.5, 6.2],
    cameraTarget: [0.0, 1.75, 0.0],
    build: buildSeashellSpecimen,
  },
  {
    id: 'violin',
    name: 'Cremonese Violin & Standing-Wave Strings',
    category: 'Acoustic Lutherie',
    summary:
      '12-segment cubic Bézier soundbox with carved f-hole apertures, logarithmic scroll volute, and four shader-displaced harmonic strings (G3, D4, A4, E5).',
    zh: {
      name: '克雷莫納小提琴與駐波琴弦',
      shortName: '克雷莫納小提琴',
      category: '製琴聲學工藝',
      summary:
        '12 段三次貝茲曲線共鳴琴身、鏤空 f 孔、對數螺旋琴頭卷軸，以及四條具備駐波頂點位移之可撥奏泛音琴弦（G3, D4, A4, E5）。',
    },
    formula: 'u(y, t) = A · sin(πy/L) · sin(2πf₀t) + 0.35A · sin(2πy/L) · cos(4πf₀t)',
    cameraPos: [0.8, 2.3, 6.6],
    cameraTarget: [0.0, 2.0, 0.0],
    build: buildViolinSpecimen,
  },
  {
    id: 'telescope',
    name: 'Keplerian Brass Refractor & Astrolabe Mount',
    category: 'Celestial Optics',
    summary:
      'Three-draw stepped brass refractor suspended inside an engraved armillary meridian cradle with high-transmission crown/flint objective optics.',
    zh: {
      name: '克卜勒黃銅折射望遠鏡與渾儀座',
      shortName: '克卜勒黃銅望遠鏡',
      category: '天體光學儀器',
      summary:
        '三節式車削黃銅鏡筒懸吊於渾儀子午環座內，配備高透光消色差雙合透鏡與體積星光錐。',
    },
    formula: '1/f = (n − 1)(1/R₁ − 1/R₂),  M = −f_objective / f_eyepiece',
    cameraPos: [4.6, 2.8, 5.2],
    cameraTarget: [0.0, 1.7, 0.0],
    build: buildTelescopeSpecimen,
  },
  {
    id: 'staircase',
    name: 'Shell-Encrusted Spiral Staircase Tower',
    category: 'Architectural Tectonics',
    summary:
      'Stepped travertine tower wrapped by 44 cantilevered stone treads, a continuous tubular brass balustrade, and instanced nacre seashell inlays.',
    zh: {
      name: '鑲嵌海螺之螺旋階梯塔',
      shortName: '螺旋海螺階梯塔',
      category: '建築構造學',
      summary:
        '三層退縮式石灰華圓塔，外繞 44 級懸挑石階、連續黃銅管狀扶手與實例化鑲嵌之珍珠母貝海螺群。',
    },
    formula: 'P(θ) = ( (R₀ − αθ)cosθ,  h·θ/(2πN),  (R₀ − αθ)sinθ )',
    cameraPos: [7.6, 4.4, 8.6],
    cameraTarget: [0.0, 2.7, 0.0],
    build: buildSpiralTowerSpecimen,
  },
];
